/** Root component — picks login vs app based on auth state. */
import { useEffect, useState } from "preact/hooks";
import type { JSX } from "preact";
import { sb } from "./supabase";
import type { Session } from "@supabase/supabase-js";
import { Login } from "./auth/Login";
import { SetPasswordModal } from "./auth/SetPasswordModal";
import { Header } from "./views/Header";
import { AdminBanner } from "./views/AdminBanner";
import { ClosureBanner } from "./views/ClosureBanner";
import { Tabs } from "./views/Tabs";
import { MonthBar } from "./views/MonthBar";
import { MyCalendar } from "./views/MyCalendar";
import { CompanyOverview } from "./views/CompanyOverview";
import { Dashboard } from "./views/Dashboard";
import { Equipment } from "./views/Equipment";
import { Directory } from "./views/Directory";
import { state, currentView, isDemoMode, isBooting } from "./state/store";
import { bootFromSupabase, shouldPromptSetPassword } from "./auth/boot";
import { BrandSprite, BgPattern } from "./ui/Brand";
import { isRealtimeConnected, unsubscribeRealtime } from "./state/realtime";
import { clearCachedState, clearSyncBaseline } from "./state/storage";
import { PW_DISMISS_KEY } from "./config";
import { startVersionCheck, updateAvailable } from "./utils/versionCheck";

export function App(): JSX.Element {
  const [signedIn, setSignedIn] = useState(false);
  // Tell open tabs when a newer bundle has been deployed (see versionCheck).
  useEffect(() => { startVersionCheck(); }, []);
  const [pwModal, setPwModal] = useState<{ open: boolean; recovery: boolean }>({
    open: false,
    recovery: false,
  });

  useEffect(() => {
    const { data: { subscription } } = sb.auth.onAuthStateChange(async (event, session: Session | null) => {
      if (isDemoMode.value) {
        setSignedIn(true);
        return;
      }

      // Handle SIGNED_IN / INITIAL_SESSION / PASSWORD_RECOVERY with the same
      // boot path. Critical: setSignedIn(true) BEFORE awaiting bootFromSupabase
      // so the UI is never trapped behind a hung network request — the Login
      // form stops showing "Signing…" the moment auth resolves, even if the
      // subsequent data fetch is slow or fails.
      const isAuthEvent =
        (event === "SIGNED_IN" || event === "INITIAL_SESSION" || event === "PASSWORD_RECOVERY") &&
        !!session;
      if (isAuthEvent && session) {
        // 1. Tear down the login screen immediately. The app shell will
        //    be visible behind the boot overlay so brand + layout don't
        //    flash empty white when the loading screen disappears.
        setSignedIn(true);

        // 2. Open the recovery modal up-front if applicable.
        if (event === "PASSWORD_RECOVERY") {
          setPwModal({ open: true, recovery: true });
        }

        // 3. Boot. While this runs (1-10s depending on calendar size),
        //    `isBooting=true` triggers a full-screen overlay in the
        //    render below — the user can't click into half-loaded UI
        //    and trigger errors against unhydrated state. We also
        //    fire the password-set check IN PARALLEL with boot so the
        //    modal can open the instant boot ends, with no extra
        //    round-trip delay tacked on after.
        //
        //    The `!isRealtimeConnected()` check skips boot on
        //    subsequent SIGNED_IN events (token refresh, etc.) — we
        //    don't re-hydrate state we already have.
        const dismissedPw = (() => {
          try {
            // Per-SESSION suppression (see config.ts). Also clear any legacy
            // localStorage flag so users who got permanently stuck under the
            // old behaviour are re-prompted from now on.
            try { localStorage.removeItem(PW_DISMISS_KEY); } catch { /* ignore */ }
            return sessionStorage.getItem(PW_DISMISS_KEY) === "1";
          } catch { return false; }
        })();
        const needPwPromise =
          event === "PASSWORD_RECOVERY"
            ? Promise.resolve(false)
            : shouldPromptSetPassword(session.user.id);
        if (!isRealtimeConnected()) {
          isBooting.value = true;
          try {
            await bootFromSupabase(session);
          } catch (err) {
            console.error("Boot failed (handled, UI stays interactive):", err);
            isBooting.value = false;
            return;
          }
          isBooting.value = false;
        }

        // 4. First-time set-password prompt (skip during recovery —
        //    that flow already opened the modal in step 2). With boot
        //    done the app shell is hydrated; we can open the modal
        //    immediately, no setTimeout grace period needed.
        if (event !== "PASSWORD_RECOVERY") {
          const needPw = await needPwPromise;
          if (needPw && !dismissedPw) {
            setPwModal({ open: true, recovery: false });
          }
        }
        return;
      }

      if (event === "SIGNED_OUT") {
        clearSyncBaseline();
        await unsubscribeRealtime();
        clearCachedState();
        setSignedIn(false);
        setPwModal({ open: false, recovery: false });
        isBooting.value = false;
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Demo mode also needs to set signedIn=true after enterDemoMode runs.
  useEffect(() => {
    const unsub = isDemoMode.subscribe((v) => {
      if (v) setSignedIn(true);
    });
    return unsub;
  }, []);

  if (!signedIn && !isDemoMode.value) {
    return (
      <>
        <BrandSprite />
        <Login />
      </>
    );
  }

  const view = currentView.value;
  const isAdmin = state.value.isAdmin;

  return (
    <div class={`app${isAdmin ? " admin-mode" : ""}`}>
      <BrandSprite />
      <BgPattern />
      {/* Skip link: hidden until tabbed-to, then jumps a keyboard user
          past the chrome and straight into the main view. */}
      <a class="skip-link" href="#main-content">
        Skip to main content
      </a>
      <Header />
      <AdminBanner />
      <ClosureBanner />
      {updateAvailable.value && (
        <div class="update-banner" role="status">
          <span>A new version of Presence Tracker is available.</span>
          <button type="button" onClick={() => location.reload()}>Reload now</button>
        </div>
      )}
      <main id="main-content" class="main">
        <Tabs />
        {/* The shared month bar drives My Calendar / Company / Directory.
            The Dashboard has its own date-range picker and Equipment picks
            dates per booking, so the bar is hidden on both to avoid a
            control that looks live but isn't. */}
        {view !== "dashboard" && view !== "equipment" && <MonthBar />}
        {view === "my" && <MyCalendar />}
        {view === "company" && <CompanyOverview />}
        {view === "dashboard" && isAdmin && <Dashboard />}
        {view === "equipment" && <Equipment />}
        {view === "directory" && <Directory />}
      </main>
      <SetPasswordModal
        open={pwModal.open}
        recovery={pwModal.recovery}
        onClose={() => setPwModal({ open: false, recovery: false })}
      />
      {isBooting.value && <BootingOverlay />}
    </div>
  );
}

/** Full-screen blocking overlay shown during bootFromSupabase. Prevents
 *  the user from interacting with partially-hydrated state during the
 *  ~5-10s it takes to fetch 44k calendar rows over the network on first
 *  sign-in. Has aria-live for screen readers and respects the brand
 *  palette (Iconic Blue + IBM Plex Mono caption). */
function BootingOverlay(): JSX.Element {
  return (
    <div class="booting-overlay" role="status" aria-live="polite">
      <div class="booting-card">
        <div class="booting-spinner" aria-hidden="true" />
        <div class="booting-text">Preparing your workspace…</div>
        <div class="booting-sub">Loading roster, calendar and presence data.</div>
      </div>
    </div>
  );
}
